<script setup>
import { computed, onMounted, ref } from 'vue';

import { useImageClipboardStore } from '@/src/store/image-clipboard-store';

const imageClipboardStore = useImageClipboardStore();

const thumbnailsContainer = ref(null);

// Computed properties
const selectedImage = computed(() => imageClipboardStore.getSelectedImage);
const sortedImages = computed(() => imageClipboardStore.getImagesSortedByDate);
const imagesCount = computed(() => imageClipboardStore.getImagesCount);

// Methods
const onDragStart = (event, image) => {
  event.dataTransfer.dropEffect = 'copy';

  event.dataTransfer.effectAllowed = 'copy';
  event.dataTransfer.setData(
    'application/json',
    JSON.stringify({
      type: 'clipboard-image',
      screenshotUID: image.screenshotUID,
    }),
  );
};

const isSelected = (screenshotUID) => {
  return selectedImage.value?.screenshotUID === screenshotUID;
};

const selectImage = (screenshotUID) => {
  imageClipboardStore.selectImage(screenshotUID);
};

const deleteImage = (screenshotUID) => {
  // Using a simple approach instead of confirm
  imageClipboardStore.deleteImage(screenshotUID);
};

const deleteSelectedImage = () => {
  if (selectedImage.value) {
    imageClipboardStore.deleteImage(selectedImage.value.screenshotUID);
  }
};

const clearAllImages = () => {
  imageClipboardStore.deleteAllImages();
};

const downloadImage = () => {
  if (!selectedImage.value) return;

  const link = document.createElement('a');
  link.href = selectedImage.value.imageDataURL;
  link.download = `${selectedImage.value.title.replace(
    /[^a-z0-9]/gi,
    '_',
  )}.png`;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
};

const formatDate = (dateString) => {
  const date = new Date(dateString);
  return date.toLocaleString();
};

const formatDateShort = (dateString) => {
  const date = new Date(dateString);
  return `${date.toLocaleDateString()} ${date.toLocaleTimeString([], {
    hour: '2-digit',
    minute: '2-digit',
  })}`;
};

onMounted(() => {
  // Auto-select the most recent image if available
  if (imagesCount.value > 0 && !selectedImage.value) {
    const mostRecent = imageClipboardStore.getMostRecentImage;
    if (mostRecent) {
      imageClipboardStore.selectImage(mostRecent.screenshotUID);
    }
  }
});
</script>

<template>
  <div class="image-clipboard-container simple-panel">
    <!-- Main viewer section -->
    <div>
      <div class="viewer-header">
        <h6>
          OCG Clipboard:
          {{ selectedImage ? selectedImage.title : 'No Image Selected' }}
        </h6>
        <div class="viewer-controls">
          <button
            v-if="selectedImage"
            class="control-btn"
            title="Download Image"
            @click="downloadImage"
          >
            <i class="fa-solid fa-download" />
          </button>
          <button
            v-if="selectedImage"
            class="control-btn delete-btn"
            title="Delete Image"
            @click="deleteSelectedImage"
          >
            <i class="fa-solid fa-trash" />
          </button>
        </div>
      </div>

      <div class="main-image-container">
        <div v-if="selectedImage" class="image-wrapper">
          <img
            :src="selectedImage.imageDataURL"
            :alt="selectedImage.title"
            class="main-image"
          >
          <div class="image-overlay viewer-panel">
            <div>
              <h6 v-if="selectedImage.description" class="text-on-viewer">
                {{ selectedImage.description }}
              </h6>
              <h6 class="text-on-viewer">
                Image Idx: {{ selectedImage.seriesIdx + 1 }}/{{
                  selectedImage.sliceIdx + 1
                }}/{{ selectedImage.imageIdx + 1 }}
              </h6>
              <div class="flex gap-1">
                <p class="text-on-viewer">
                  {{ formatDate(selectedImage.dateTaken) }}
                </p>

                <p
                  v-if="selectedImage.width && selectedImage.height"
                  class="text-on-viewer"
                >
                  | ({{ selectedImage.width }}x{{ selectedImage.height }})
                </p>
              </div>
            </div>
            <div
              v-if="selectedImage.tags && selectedImage.tags.length > 0"
              class="tags-section"
            >
              <div class="tags-container">
                <span v-for="tag in selectedImage.tags" :key="tag" class="tag">
                  {{ tag }}
                </span>
              </div>
            </div>
          </div>
        </div>

        <div v-else class="no-image-placeholder">
          <i class="fa-solid fa-image placeholder-icon" />
          <p>No screenshots captured yet</p>
        </div>
      </div>
    </div>

    <!-- Thumbnails gallery section -->
    <div class="thumbnails-section simple-panel">
      <div class="thumbnails-header">
        <h6>Screenshots ({{ imagesCount }})</h6>
        <div class="gallery-controls">
          <button
            v-if="imagesCount > 0"
            class="control-btn clear-all-btn"
            title="Clear All"
            @click="clearAllImages"
          >
            <i class="fa-solid fa-trash" />
            Clear All
          </button>
        </div>
      </div>

      <div ref="thumbnailsContainer" class="thumbnails-gallery">
        <div
          v-for="image in sortedImages"
          :key="image.screenshotUID"
          class="thumbnail-item" :class="[
            { selected: isSelected(image.screenshotUID) },
          ]"
          draggable="true"
          tabindex="0"
          role="button"
          :aria-label="`Select screenshot ${image.title}`"
          @dragstart="onDragStart($event, image)"
          @click="selectImage(image.screenshotUID)"
          @keydown.enter="selectImage(image.screenshotUID)"
          @keydown.space="selectImage(image.screenshotUID)"
        >
          <div class="thumbnail-wrapper">
            <img
              :src="image.thumbnailDataURL"
              :alt="image.title"
              class="thumbnail-image"
            >
            <div class="thumbnail-overlay">
              <button
                class="thumbnail-delete-btn"
                title="Delete"
                @click.stop="deleteImage(image.screenshotUID)"
              >
                <i class="fa-solid fa-times" />
              </button>
            </div>
          </div>

          <div class="thumbnail-info">
            <p class="thumbnail-title">
              {{ image.title }}
            </p>
            <p class="thumbnail-date">
              {{ formatDateShort(image.dateTaken) }}
            </p>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<style scoped>
.image-clipboard-container {
  display: flex;
  flex-direction: column;
  height: 100%;
  max-width: 412px;
  width: 412px;
  min-width: 412px;
  border-radius: 10px;
}

.main-viewer {
  display: flex;
  flex-direction: column;
  min-height: 0;
}

.viewer-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 5px 10px;
}

.viewer-controls {
  display: flex;
  gap: 8px;
}

.control-btn {
  background: #3a3a3a;
  border: 1px solid #555;
  color: #ffffff;
  padding: 4px 8px;
  border-radius: 4px;
  cursor: pointer;
  font-size: 10px;
  transition: all 0.2s ease;
}

.control-btn:hover {
  background: #4a4a4a;
  border-color: #666;
}

.delete-btn:hover {
  background: #ff4444;
  border-color: #ff6666;
}

.capture-btn {
  background: #0066cc;
  border-color: #0088ff;
}

.capture-btn:hover {
  background: #0088ff;
}

.main-image-container {
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 5px;
  min-height: 350px;
}

.image-wrapper {
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
}

.main-image {
  object-fit: contain;
  width: 100%;
  height: 100%;
  height: 400px;
}

.image-overlay {
  position: absolute;
  bottom: 10px;
  left: 10px;
  right: 10px;
  padding: 10px;
  border-radius: 6px;
  display: flex;
  justify-content: space-between;
  align-items: flex-end;
}

.tags-section {
  align-self: flex-start;
}

.tags-container {
  display: flex;
  flex-wrap: wrap;
  gap: 4px;
  max-width: 200px;
}

.tag {
  background: rgba(255, 255, 255, 0.8);
  color: black;
  padding: 2px 6px;
  border-radius: 10px;
  font-size: 10px;
  font-weight: 500;
  white-space: nowrap;
}

.date-taken {
  margin: 0 0 4px 0;
  font-weight: 500;
}

.description {
  margin: 0 0 4px 0;
  opacity: 0.8;
}

.dimensions {
  margin: 0;
  opacity: 0.6;
  font-size: 11px;
}

.no-image-placeholder {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  color: #666;
  text-align: center;
}

.placeholder-icon {
  font-size: 48px;
  margin-bottom: 15px;
  opacity: 0.5;
}

.capture-first-btn {
  background: #0066cc;
  border: none;
  color: white;
  padding: 12px 20px;
  border-radius: 6px;
  cursor: pointer;
  margin-top: 15px;
  font-size: 14px;
  display: flex;
  align-items: center;
  gap: 8px;
  transition: background 0.2s ease;
}

.capture-first-btn:hover {
  background: #0088ff;
}

.thumbnails-section {
  flex: 1;
  display: flex;
  flex-direction: column;
  border-radius: 0px;
}

.thumbnails-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 10px 15px;
}

.clear-all-btn {
  background: #ff4444;
  border-color: #ff6666;
}

.clear-all-btn:hover {
  background: #ff6666;
}

.thumbnails-gallery {
  display: grid;
  justify-content: center;
  align-content: flex-start;
  height: 100%;
  overflow: auto;
  grid-template-columns: repeat(auto-fill, minmax(var(--small-thumbnail-width), 1fr));
  row-gap: 10px;
  gap: 5px;
  padding: 5px;
}

.thumbnails-gallery::-webkit-scrollbar {
  height: 6px;
}

.thumbnails-gallery::-webkit-scrollbar-track {
  background: #1a1a1a;
}

.thumbnails-gallery::-webkit-scrollbar-thumb {
  background: #555;
  border-radius: 3px;
}

.thumbnail-item {
  flex-shrink: 0;
  width: 120px;
  cursor: pointer;
  transition: transform 0.2s ease;
}

.thumbnail-item:hover {
  transform: translateY(-2px);
}

.thumbnail-item.selected .thumbnail-wrapper {
  border-color: #0088ff;
  box-shadow: 0 0 0 2px rgba(0, 136, 255, 0.3);
}

.thumbnail-wrapper {
  position: relative;
  width: 120px;
  height: 80px;
  border: 2px solid #444;
  border-radius: 6px;
  overflow: hidden;
  background: #1a1a1a;
}

.thumbnail-image {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.thumbnail-overlay {
  position: absolute;
  top: 4px;
  right: 4px;
  opacity: 0;
  transition: opacity 0.2s ease;
}

.thumbnail-item:hover .thumbnail-overlay {
  opacity: 1;
}

.thumbnail-delete-btn {
  background: rgba(255, 68, 68, 0.9);
  border: none;
  color: white;
  width: 20px;
  height: 20px;
  border-radius: 50%;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 10px;
}

.thumbnail-info {
  padding: 6px 4px;
}

.thumbnail-title {
  margin: 0 0 2px 0;
  font-size: 11px;
  font-weight: 500;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.thumbnail-date {
  margin: 0;
  font-size: 9px;
  opacity: 0.6;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
</style>
